import { useEffect } from 'react';
import { IonItem, IonItemDivider, IonCheckbox } from '@ionic/react';

import forceUnits from 'config/force-units';
import pressureUnits from 'config/pressure-units';
import { toSignificant } from 'lib/number';
import { getValue } from 'helpers/events';
import useModel from 'hooks/useModel';
import MagnumInput from 'components/helpers/MagnumInput';
import PushPierMeasurements from './PushPierMeasurements';

const psi = pressureUnits["psi"];
const kips = forceUnits["kips"];

const DEFAULT_LOG = {
  pileNo: '',
  measurements: null,
  proofTestPressure: '',
  proofTestLoad: '',
  holdDuration: '',
  creep: false,
  finalDepth: '',
  appliedLoad: '',
  designLoad: '',
  installRate: '',
};

const defaultFromLoad = (load, pileNumber) => ({
  ...DEFAULT_LOG,
  pileNo: pileNumber,
  designLoad: (toSignificant(kips.toDisplay(load.compression), 3) || '').toLocaleString()
});

export default function PushPierLog({load, log, pileNumber, depthIncrement, updateLog})  {
  const [logFields, setLogsProperty, __, setState] = useModel(() => (
    _.pick(log || defaultFromLoad(load, pileNumber), _.keys(DEFAULT_LOG))
  ));

  useEffect(() => {
    if (log === null) setState(defaultFromLoad(load));
  }, [log]);

  const setLog = _.curry((key, value) => {
    if (!log) updateLog(logFields);
    setLogsProperty(key, value);
    updateLog({[key]: value});
  });

  return <>
    <IonItem>
      <MagnumInput label="Pier Number"
        labelPlacement="start"
        value={logFields.pileNo ?? pileNumber}
        onChange={getValue(setLog('pileNo'))}/>
    </IonItem>
    <IonItemDivider>
      Periodic Measurements
    </IonItemDivider>

    <PushPierMeasurements
      load={load}
      depthIncrement={depthIncrement}
      measurements={logFields.measurements}
      onChange={setLog('measurements')}/>

    <IonItemDivider>
      Proof Test Values
    </IonItemDivider>
    <IonItem>
      <MagnumInput label="Proof Test Pressure"
        labelPlacement="start"
        value={logFields.proofTestPressure}
        onChange={getValue(setLog('proofTestPressure'))}/>
        {psi.abbreviation}
    </IonItem>
    <IonItem>
      <MagnumInput label="Proof Test Load"
        labelPlacement="start"
        value={logFields.proofTestLoad}
        onChange={getValue(setLog('proofTestLoad'))}/>
        {kips.abbreviation}
    </IonItem>
    <IonItem>
      <MagnumInput label="Hold Duration"
        labelPlacement="start"
        value={logFields.holdDuration}
        onChange={getValue(setLog('holdDuration'))}/>
    </IonItem>
    <IonItem>
      <IonCheckbox
        labelPlacement="start"
        justify="space-between"
        value={logFields.creep}
        onValueChange={setLog('creep')}
      >
        Creep &lt; 1/16 in/hr
      </IonCheckbox>
    </IonItem>

    <IonItemDivider>
      Final Installation Values
    </IonItemDivider>
    <IonItem>
      <MagnumInput label="Depth"
        labelPlacement="start"
        value={logFields.finalDepth}
        onChange={getValue(setLog('finalDepth'))}/>
        ft
    </IonItem>
    <IonItem>
      <MagnumInput label="Applied Load"
        labelPlacement="start"
        value={logFields.appliedLoad}
        onChange={getValue(setLog('appliedLoad'))}/>
      {kips.abbreviation}
    </IonItem>
    <IonItem>
      <MagnumInput label="Design Load"
        labelPlacement="start"
        value={logFields.designLoad}
        onChange={getValue(setLog('designLoad'))}/>
      {kips.abbreviation}
    </IonItem>
    <IonItem>
      <MagnumInput label="Install Rate"
        labelPlacement="start"
        value={logFields.installRate}
        onChange={getValue(setLog('installRate'))}/>
    </IonItem>
  </>
}
